import React, { useState } from "react";
import { Users } from "lucide-react";
import { LeaderboardUserEntry } from "@crypto-trading/common/browser";
import { useLaunchParams } from "@telegram-apps/sdk-react";

interface LeaderboardTabProps {
  leaderboard: LeaderboardUserEntry[];
  referralCount: number;
  userRank: number | null | undefined;
  userPoints: number;
}

export const LeaderboardTab: React.FC<LeaderboardTabProps> = ({
  leaderboard,
  referralCount,
  userRank,
  userPoints,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const launchParams = useLaunchParams();
  const user = launchParams.initData?.user;
  const userId = user?.id;

  const displayName = (entry: LeaderboardUserEntry) => {
    if (entry?.username) {
      return `${entry?.username}`;
    }
    if (entry?.firstName) {
      return entry?.firstName + (entry?.lastName ? ` ${entry?.lastName}` : "");
    }
    return ``;
  };

  const topReferrers = leaderboard.slice(0, 3);

  // Check if current user is already in the leaderboard
  const isUserInLeaderboard = leaderboard.some(
    (entry) => entry.userId === userId?.toString()
  );
  const shouldShowUserEntry =
    userRank && !isUserInLeaderboard && userRank > leaderboard.length;

  return (
    <div className="p-4 md:p-0 space-y-6">
      {/* Preload image - always rendered but hidden */}
      <img
        src="/leaderboard.png"
        alt=""
        className="hidden"
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(true)} // Show content even if image fails to load
      />

      {/* Top section - always rendered to reserve space */}
      <div
        className="p-4 gap-3 rounded-2xl border border-border flex flex-col items-center"
        style={{
          background: "linear-gradient(135deg, #00332180 0%, #00805300 100%)",
        }}
      >
        <div className="w-full flex justify-center">
          <div className="relative w-full max-w-[442px]">
            {imageLoaded ? (
              <>
                <img
                  src="/leaderboard.png"
                  alt="Leaderboard"
                  className="w-full h-auto aspect-[4/3]"
                />
                <div className="absolute left-[51%] top-1/2 w-10 -translate-x-1/2 -translate-y-1 flex flex-col items-center justify-center">
                  <div className="text-white text-sm">
                    {displayName(topReferrers[0])}
                  </div>
                  <div className="text-lg font-bold text-accent-light">
                    {topReferrers[0]?.points}
                  </div>
                </div>
                <div className="absolute left-[13%] top-[70%] w-10 -translate-x-1/2 -translate-y-1 flex flex-col items-center justify-center">
                  <div className="text-white text-sm">
                    {displayName(topReferrers[1])}
                  </div>
                  <div className="text-lg font-bold text-[#009BD6]">
                    {topReferrers[1]?.points}
                  </div>
                </div>
                <div className="absolute right-[2.5%] w-10 top-[70%] -translate-x-1/2 -translate-y-1 flex flex-col items-center justify-center">
                  <div className="text-white text-sm">
                    {displayName(topReferrers[2])}
                  </div>
                  <div className="text-lg font-bold text-[#6568FF]">
                    {topReferrers[2]?.points}
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full aspect-[4/3] rounded-lg flex items-center justify-center">
                <svg
                  className="size-5 animate-spin animate-pulse"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#29ffb4"
                    d="M12 22c5.523 0 10-4.477 10-10h-2c0 4.418-3.582 8-8 8s-8-3.582-8-8c0-4.418 3.582-8 8-8V2C6.477 2 2 6.477 2 12c0 5.523 4.477 10 10 10z"
                  />
                </svg>
              </div>
            )}
          </div>
        </div>
        <div className="inline-flex items-center px-3 py-1 bg-[#13161b] border border-[#373a41] rounded-full mt-2 w-full">
          <span className="text-text-secondary text-sm font-medium">
            You've referred{" "}
            <span className="text-accent-light">{referralCount}</span> friends –
            keep it going!
          </span>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="space-y-3 min-h-[50vh]">
        {leaderboard.length > 0 ? (
          <div className="bg-tertiary border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-2 text-sm text-text-secondary flex justify-between border-b border-border">
              <span className="font-medium">Board</span>
              <span className="font-medium">Points</span>
            </div>
            <ul>
              {leaderboard.map((entry) => (
                <li
                  key={entry.userId}
                  className="border-t border-border px-4 py-3 flex justify-between items-center"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-text-secondary w-6 text-start">
                      {entry.rank}.
                    </span>
                    <span className="text-text-primary font-medium">
                      {displayName(entry)}
                    </span>
                    {entry.userId === userId?.toString() && (
                      <span className="text-xs border border-text-secondary text-text-secondary rounded-full px-2 py-0.5 ml-2 font-normal">
                        you
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-text-primary font-medium">
                      {entry.points}
                    </span>
                  </div>
                </li>
              ))}
              {shouldShowUserEntry && (
                <>
                  <li className="border-t border-border px-4 py-2 flex justify-center items-center">
                    <span className="text-text-secondary text-xs">• • •</span>
                  </li>
                  <li className="border-t border-border px-4 py-3 flex justify-between items-center bg-accent/5">
                    <div className="flex items-center gap-3">
                      <span className="text-text-secondary w-6 text-start">
                        {userRank}.
                      </span>
                      <span className="text-text-primary font-medium">
                        {user?.username
                          ? user.username
                          : (user?.firstName || "") +
                            (user?.lastName ? ` ${user.lastName}` : "")}
                      </span>
                      <span className="text-xs border border-accent text-accent rounded-full px-2 py-0.5 ml-2 font-normal">
                        you
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-text-primary font-medium">
                        {userPoints}
                      </span>
                    </div>
                  </li>
                </>
              )}
            </ul>
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-telegram-hint mx-auto mb-4" />
            <p className="text-telegram-hint">No referrals yet</p>
            <p className="text-sm text-telegram-hint mt-1">
              Be the first to refer friends and claim the top spot!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
